import type { components } from "../../../api/generated";
import type {
  SysDeptMutation,
  SysStaffListParams,
  SysStaffAddDeptMutation,
  SysStaffMutation,
  SysStaffMoveDeptMutation,
  SysStaffPasswordResetMutation,
  SysStaffStatusMutation,
  SysStaffSupervisorMutationItem,
} from "../types";

export type SysDeptTreePayloadRaw = components["schemas"]["SysDeptTreePayload"];
export type SysDeptTreeNodeRaw = components["schemas"]["SysDeptTreeNode"];
export type SysDeptStaffTreeItemRaw = components["schemas"]["SysDeptStaffTreeItem"];
export type SysStaffListPayloadRaw = components["schemas"]["SysStaffListPayload"];
export type SysStaffItemRaw = components["schemas"]["SysStaffItem"];
export type SysStaffSupervisorPayloadRaw = components["schemas"]["SysStaffSupervisorPayload"];
export type SysStaffSupervisorRowRaw = components["schemas"]["SysStaffSupervisorRow"];
export type SysStaffPasswordResetPayloadRaw = components["schemas"]["AdminResetPasswordPayload"];

/** 映射用户列表查询参数。 */
export function mapSysStaffListQueryParams(params: SysStaffListParams) {
  return {
    dept_id: params.deptId,
    is_staff: params.isStaff,
    keyword: normalizeOptionalText(params.keyword),
    phone: normalizeOptionalText(params.phone),
    page: params.page,
    page_size: params.pageSize,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 映射部门变更载荷。 */
export function mapSysDeptMutationPayload(payload: SysDeptMutation) {
  return {
    dept_name: payload.deptName.trim(),
    parent_id: payload.parentId,
    order_num: payload.orderNum,
    leader_id: payload.leaderId,
    approver_id: payload.approverId,
    phone: payload.phone.trim(),
    email: payload.email.trim(),
    status: payload.status,
  };
}

/** 映射用户变更载荷。 */
export function mapSysStaffMutationPayload(payload: SysStaffMutation) {
  return {
    username: payload.username.trim(),
    realname: payload.realname.trim(),
    nickname: payload.nickname.trim(),
    phone: payload.phone.trim(),
    email: payload.email.trim(),
    avatar: payload.avatar.trim(),
    sex: payload.sex,
    main_dept_id: payload.mainDeptId,
    dept_ids: payload.deptIds,
    status: payload.status,
    user_type: payload.userType,
    supplier_id: payload.supplierId,
    wx_openid: payload.wxOpenid.trim(),
    wx_unionid: payload.wxUnionid.trim(),
    is_subscribe: payload.isSubscribe,
    ...(payload.isSuperuser === undefined ? {} : { is_superuser: payload.isSuperuser }),
    is_staff: payload.isStaff,
    supervisors: payload.supervisors.map(mapSysStaffSupervisorMutationItem),
  };
}

/** 映射用户状态变更载荷。 */
export function mapSysStaffStatusPayload(payload: SysStaffStatusMutation) {
  return {
    status: payload.status,
  };
}

/** 映射员工移动部门载荷。 */
export function mapSysStaffMoveDeptPayload(payload: SysStaffMoveDeptMutation) {
  return {
    dept_id: payload.deptId,
  };
}

/** 映射员工追加归属部门载荷。 */
export function mapSysStaffAddDeptPayload(payload: SysStaffAddDeptMutation) {
  return {
    dept_id: payload.deptId,
  };
}

/** 映射管理员重置密码载荷。 */
export function mapSysStaffPasswordResetPayload(payload: SysStaffPasswordResetMutation) {
  return {
    password: payload.password,
  };
}

/** 映射主管变更载荷。 */
export function mapSysStaffSupervisorsPayload(payload: SysStaffSupervisorMutationItem[]) {
  return payload.map(mapSysStaffSupervisorMutationItem);
}

/** 映射单条主管关系变更项。 */
function mapSysStaffSupervisorMutationItem(payload: SysStaffSupervisorMutationItem) {
  return {
    supervisor_id: payload.supervisorId,
    supervisor_level: payload.supervisorLevel,
    order_num: payload.orderNum,
  };
}

/** 归一化可选文本参数。 */
function normalizeOptionalText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}
